<template>
  <card :style="{width}" :overflow-off="false" :class="[baseCss, height ? `${baseCss}-scrollable` : null]">
    <card-text>
      <div :class="[`${baseCss}-wrapper`]">
        <card v-if="searchable" :overflow-off="false">
          <card-text>
            <table-layout y-align="middle">
              <row>
                <cell :class="[searchInOptions.length ? 'pr-2' : null]">
                  <text-field ref="search" :color="color" :color-lightness="colorLightness" clearable :icon-left="searchableIcon" :placeholder="searchableLabel" v-model="search" />
                </cell>
                <cell v-if="searchInOptions.length">
                  <dropdown @change="focusSearchField" :width="searchInOptionsWidth" :items="searchInOptions" v-model="searchInSelection" :color="color" :color-lightness="colorLightness" />
                </cell>
              </row>
            </table-layout>
          </card-text>
        </card>
        <divider v-if="searchable" />
        <table cellpadding="0" cellspacing="0" :class="[fixed ? 'bcq-data-table-fixed' : null]">
          <thead v-if="header" :class="[height ? 'bcq-elevation-2' : null, `${baseCss}-headers`, 'select-none']">
          <tr>
            <th v-if="selectableRows" :class="[dense ? 'p-2' : 'p-3', `${baseCss}-selectable-cell`, 'text-center']">
              <checkbox :icon-color="color" :icon-color-lightness="colorLightness" :indeterminate="indeterminate" v-if="!singleSelectableRows" v-model="selectable" />
            </th>
            <th :style="{width: header.width}" :class="[dense ? 'p-2' : 'p-3', `text-${header.align || 'left'}`, header.cssClasses ? header.cssClasses.join(' ') : null]" v-for="(header, index) in headers" :key="`th-${index}`" v-html="header.text" />
            <th v-if="expandableRows" :class="[dense ? 'p-2' : 'p-3', `${baseCss}-selectable-cell`, 'text-center']" />
          </tr>
          </thead>
          <tbody :style="{height}" :class="[`${baseCss}-body`, height ? 'shadow-md' : null]" v-if="filteredItems.length">
            <template v-for="(item, index) in itemsPerPage ? filteredItems.slice(page * itemsPerPageInternal[0], page * itemsPerPageInternal[0] + itemsPerPageInternal[0]) : filteredItems">
              <tr :key="`tr-${index}`" :class="[item.selected ? `${baseCss}-row-selected` : null, 'transition-all', 'duration-300']">
                <td v-if="selectableRows" :class="[dense ? 'p-2' : 'p-3', `${baseCss}-selectable-cell`, 'text-center']">
                  <checkbox :icon-color="color" :icon-color-lightness="colorLightness" @input="rowSelectionToggle($event, index)" v-model="item.selected" />
                </td>
                <template v-for="(key, i) in headerKeysArray">
                  <td v-if="item[key] && typeof item[key] !== 'object'" @click="editableColumns.includes(key) ? null : $emit('row-click', {item})" @dblclick="editableColumns.includes(key) ? editColumn($event, item, key) : null" :key="`${item[key]}-${index}-${i}`" :class="[editableColumns.includes(key) ? [`${baseCss}-column-editable`, 'cursor-pointer'] : null, dense ? 'p-2' : 'p-3', `text-${headers[i].align || 'left'}`]" v-html="item[key]" />
                  <td @click="$emit('row-click', {item})" :key="`obj-${i}`" v-else-if="item[key] !== 'object'" :class="[dense ? 'p-2' : 'p-3', `text-${headers[i].align || 'left'}`]">
                    <slot v-if="['btn', 'avatar', 'icon', 'flag', 'dropdown', 'router-link', 'a', 'chip', 'lazy-image', 'radio-group', 'html'].includes(item[headerKeysArray[i]].component)" :name="item[headerKeysArray[i]].component" v-bind="item[key]" />
                  </td>
                </template>
                <td v-if="expandableRows" :class="[dense ? 'p-2' : 'p-3', `${baseCss}-selectable-cell`, 'text-center']">
                  <btn size="sm" color="white" :color-lightness="null" :bg-color="color" :bg-color-lightness="colorLightness" :icon="`chevron-${item.expanded ? 'up' : 'down'}`" @click="item.expanded = !item.expanded" />
                </td>
              </tr>
              <tr @dblclick="editableColumns.includes('expandableRow') ? editColumn($event, item, 'expandableRow') : null" v-if="expandableRows && item.expandableRow" :key="`editable-${index}`" :class="[editableColumns.includes('expandableRow') ? [`${baseCss}-column-editable`, 'cursor-pointer'] : null, `${baseCss}-row-expandable-row`, 'transition-all', 'duration-500', !item.expanded ? ['absolute', 'opacity-0', 'clip-0'] : [`${baseCss}-row-expandable-row-visible`, 'opacity-1', 'static'], item.selected ? `${baseCss}-row-selected` : null, 'transition-all-25']">
                <td :class="[dense ? 'p-2' : 'p-3']" :colspan="colspan" v-html="item.expandableRow" />
              </tr>
            </template>
          </tbody>
          <tbody v-else :style="{height}">
          <tr>
            <td :class="[dense ? 'p-2' : 'p-3', 'text-center', 'bcq-disabled']" :colspan="colspan" v-html="noData" />
          </tr>
          </tbody>
          <tfoot v-if="footer" :class="[`${baseCss}-footer`, 'select-none']"></tfoot>
        </table>
      </div>
      <card v-if="itemsPerPage !== null" :overflow-off="false" :class="['mt-3']">
        <card-text>
          <table-layout y-align="middle">
            <row>
              <cell>
                <pager color="white" :dense="dense" :color-lightness="null" :bg-color="color" :bg-color-lightness="colorLightness" v-model="page" :count="itemsLength" size="xs" :first-last-buttons="pagerFirstLastButtons" :prev-next-buttons="pagerPrevNextButtons" :page-buttons="pagerButtons" />
              </cell>
              <cell x-align="right">
                <dropdown :direction="itemsPerPageDropdownDirection" :color="color" :color-lightness="colorLightness" width="128px" :items="itemsPerPageDropdown" v-model="itemsPerPageInternal" />
              </cell>
            </row>
          </table-layout>
        </card-text>
      </card>
      <div :class="[`${baseCss}-column-editor-overlay`, 'fixed', 'top-0', 'left-0', 'w-full', 'h-full']" v-if="editableColumns.length && inlineEditor" />
      <card :class="[`${baseCss}-column-editor-editbox`, 'absolute']" :style="inlineEditorStyle" v-if="editableColumns.length && inlineEditor">
        <card-text>
          <form @submit.prevent="saveEditedCell">
            <text-field :color="color" :color-lightness="colorLightness" :multiline="currentEditedCell.length > 64" v-model="currentEditedCell" :autofocus="true" icon-left="table-edit" />
            <table-layout equal-cells :class="['mt-3']">
              <row>
                <cell :class="['pr-1']">
                  <btn :color="$dark ? 'white' : 'black'" :color-lightness="null" @click="hideInlineEditor" plain block :tile="cancel" size="xs">{{ cancel }}</btn>
                </cell>
                <cell :class="['pl-1']">
                  <btn color="white" :color-lightness="null" size="xs" :bg-color="color" :bg-color-lightness="colorLightness" type="button" @click="saveEditedCell" block :title="ok">{{ ok }}</btn>
                </cell>
              </row>
            </table-layout>
          </form>
        </card-text>
      </card>
    </card-text>
  </card>
</template>
<script>
import methods from '../../mixins/methods'
import props from '../../mixins/props'

export default {
  name: 'DataTable',
  mixins: [
    props,
    methods
  ],
  data () {
    return {
      search: '',
      searchInSelection: [],
      searchInOptionsWidth: '128px',
      page: 0,
      itemsInternal: [],
      itemsPerPageInternal: [],
      inlineEditorStyle: null,
      inlineEditor: false,
      currentEditedCell: null,
      currentEditedCellItemIndex: null,
      currentEditedCellKey: null,
      selectable: false,
      indeterminate: false,
      baseCss: this.componentBaseCss('data-table', [])
    }
  },
  computed: {
    itemsLength () {
      if (this.itemsPerPageInternal[0]) {
        return Math.ceil(this.filteredItems.length / this.itemsPerPageInternal[0])
      }
    },
    filteredItems () {
      const searchIn = this.searchIn
      const searchInSelection = this.searchInSelection
      const search = this.search.toLowerCase()
      return this.itemsInternal.filter(item => {
        if (searchIn !== '__all__') {
          return (searchIn ? item[searchInSelection.length ? searchInSelection[0] : searchIn].toString() : Object.values(item).join('')).toLowerCase().indexOf(search) > -1
        } else {
          const joined = Object.values(item).join('')
          return joined.toLowerCase().indexOf(search) > -1
        }
      })
    },
    headerKeysArray () {
      const keys = []
      this.headers.forEach(header => {
        keys.push(header.value)
      })
      return keys
    },
    colspan () {
      let colspan = this.headers.length
      if (this.selectableRows) {
        colspan++
      }
      if (this.expandableRows) {
        colspan++
      }
      return colspan
    }
  },
  props: {
    color: {
      type: String
    },
    colorLightness: {
      type: Number
    },
    pagerButtons: {
      type: Boolean
    },
    pagerPrevNextButtons: {
      type: Boolean,
      default: true
    },
    pagerFirstLastButtons: {
      type: Boolean,
      default: true
    },
    headers: {
      type: Array,
      default: () => []
    },
    items: {
      type: Array,
      default: () => []
    },
    itemsPerPageDropdownDirection: {
      type: String,
      default: 'down',
      validator: value => {
        return [
          'down',
          'up'
        ].includes(value)
      }
    },
    itemsPerPageDropdown: {
      type: Array,
      default: () => [{
        text: '5',
        value: 5
      }, {
        text: '10',
        value: 10
      }, {
        text: '20',
        value: 20
      }, {
        text: '30',
        value: 30
      }, {
        text: '40',
        value: 40
      }, {
        text: '50',
        value: 50
      }, {
        text: '100',
        value: 100
      }]
    },
    searchableIcon: {
      type: String,
      default: 'magnify'
    },
    noData: {
      type: String,
      default: 'No matching records found'
    },
    ok: {
      type: String,
      default: 'Ok'
    },
    cancel: {
      type: String,
      default: 'Cancel'
    },
    searchableLabel: {
      type: String
    },
    fixed: {
      type: Boolean,
      default: true
    },
    header: {
      type: Boolean,
      default: true
    },
    footer: {
      type: Boolean,
      default: true
    },
    dense: {
      type: Boolean
    },
    selectableRows: {
      type: Boolean
    },
    singleSelectableRows: {
      type: Boolean
    },
    searchable: {
      type: Boolean
    },
    searchIn: {
      type: String
    },
    searchInOptions: {
      type: Array,
      default () {
        return []
      }
    },
    height: {
      type: String
    },
    width: {
      type: String
    },
    flagsDir: {
      type: String
    },
    expandableRows: {
      type: Boolean
    },
    itemsPerPage: {
      type: Number,
      default: null
    },
    editableColumns: {
      type: Array,
      default: () => []
    },
    inlineEditorWidth: {
      type: String,
      default: '256px'
    },
    sortable: {
      type: Boolean
    }
  },
  watch: {
    selectable () {
      this.itemsInternal.map(item => {
        item.selected = this.selectable
      })
      this.indeterminate = false
      this.emitRowSelection()
    },
    items () {
      this.setItemsInternally()
    },
    search () {
      this.page = 0
    }
  },
  mounted () {
    this.prepare()
  },
  methods: {
    focusSearchField () {
      this.$refs['search'].$el.querySelector('input').focus()
    },
    prepare () {
      this.setItemsInternally()
      this.setItemsPerPageInternally()
      this.prepareSearch()
    },
    prepareSearch () {
      const searchIn = this.searchIn
      const searchInOptionsWidth = Math.max(...(this.searchInOptions.map(item => {
        return item.text
      }).map(el => el.length)))
      this.searchInOptionsWidth = 12 * searchInOptionsWidth + 'px'
      if (this.searchInOptions.length && searchIn !== '' && this.searchable) {
        this.searchInSelection = [searchIn]
      }
    },
    setItemsPerPageInternally () {
      this.itemsPerPageInternal = [this.itemsPerPage]
    },
    setItemsInternally () {
      this.itemsInternal = this.items
    },
    rowSelectionToggle ($event, index) {
      if (this.singleSelectableRows) {
        this.itemsInternal.map((item, i) => {
          i === index ? item.selected = $event : item.selected = false
        })
      }
      if (this.itemsInternal.length === this.getSelected().length) {
        this.selectable = true
        this.indeterminate = false
      } else if (this.getSelected().length === 0) {
        this.selectable = false
        this.indeterminate = false
      } else {
        this.indeterminate = true
      }
      this.emitRowSelection()
    },
    getSelected () {
      return this.itemsInternal.filter(item => item.selected)
    },
    emitRowSelection () {
      this.$emit('row-selection', this.itemsInternal.filter(item => item.selected))
    },
    editColumn ($event, item, key) {
      this.inlineEditorStyle = {
        top: $event.layerY + 'px',
        left: $event.layerX + 'px',
        width: this.inlineEditorWidth
      }
      this.currentEditedCellItemIndex = this.itemsInternal.findIndex(row => row === item)
      this.currentEditedCell = item[key]
      this.currentEditedCellKey = key
      this.inlineEditor = true
    },
    hideInlineEditor () {
      this.inlineEditor = false
      this.inlineEditorStyle = null
      this.currentEditedCell = null
      this.currentEditedCellItemIndex = null
      this.currentEditedCell = null
      this.currentEditedCellKey = null
    },
    saveEditedCell () {
      const items = this.itemsInternal
      const index = this.currentEditedCellItemIndex
      items[index][this.currentEditedCellKey] = this.currentEditedCell
      this.$emit('input', {items, index})
      this.hideInlineEditor()
    }
  }
}
</script>
<style>
.bcq-data-table
{
  border-radius: 4px;
}

.bcq-data-table table
{
  border-collapse: collapse;
  width: 100%;
}

.bcq-data-table .bcq-data-table-fixed
{
  table-layout: fixed;
}

.bcq-data-table tr:not(.bcq-data-table-row-expandable-row)
{
  border-bottom: thin solid rgba(0, 0, 0, .12);
}

.bcq-data-table thead tr:last-of-type
{
  border-bottom-width: 3px;
}

.bcq-ui-theme-dark .bcq-data-table tr:not(.bcq-data-table-row-expandable-row)
{
  border-bottom-color: rgba(255, 255, 255, .5);
}

.bcq-data-table.bcq-data-table-scrollable thead tr:last-of-type,
.bcq-data-table tbody tr:last-of-type
{
  border-bottom: 0;
}

.bcq-data-table thead th:not(.bcq-data-table-selectable-cell)
{
  font-size: 12px;
  font-weight: 700;
}

.bcq-data-table tbody td:not(.bcq-data-table-selectable-cell)
{
  font-size: 14px;
  font-weight: 400;
}

.bcq-data-table .bcq-data-table-selectable-cell
{
  width: 56px;
}

.bcq-data-table .bcq-data-table-selectable-cell .bcq-checkbox
{
  padding: 0;
}

.bcq-data-table thead:empty,
.bcq-data-table tfoot:empty
{
  display: none;
}

.bcq-data-table.bcq-data-table-scrollable tbody
{
  display: block;
  overflow: auto;
}
.bcq-data-table.bcq-data-table-scrollable thead,
.bcq-data-table.bcq-data-table-scrollable tbody tr
{
  display: table;
  width: 100%;
  table-layout: fixed;
}
.bcq-data-table.bcq-data-table-scrollable thead
{
  width: calc(100% - 1em)
}
.bcq-data-table.bcq-data-table-scrollable table
{
  width: 100%;
}

.bcq-data-table td,
.bcq-data-table th
{
  vertical-align: middle;
}

.bcq-data-table .bcq-data-table-column-editor-overlay
{
  z-index: 10000;
  background: var(--semi-transparent-light);
}

.bcq-ui-theme-dark .bcq-data-table .bcq-data-table-column-editor-overlay
{
  background: var(--semi-transparent-dark);
}

.bcq-data-table .bcq-data-table-column-editor-editbox
{
  z-index: 10500;
}

.bcq-data-table tbody tr:hover
{
  background: var(--router-link-active);
}

.bcq-ui-theme-dark .bcq-data-table tbody tr:hover
{
  background: var(--router-link-active-dark);
}

.bcq-data-table .bcq-data-table-column-editable
{
  text-decoration-line: underline;
  text-decoration-style: double;
}
</style>



// WEBPACK FOOTER //
// src/bcq-ui/components/data-table/data-table.vue